import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * DropdownSection — collapsible card section
 *
 * Props:
 *   title        {string}          Section heading text
 *   titleStyle   {string}          Extra Tailwind classes for the title text
 *   icon         {React.Component} Lucide or react-icons icon component
 *   iconStyle    {string}          Extra Tailwind classes for the icon
 *   defaultOpen  {boolean}         Start expanded (default: true)
 *   children     {React.Node}      Content rendered inside the section
 */
const DropdownSection = ({
    title,
    titleStyle = "",
    icon: Icon,
    iconStyle = "",
    subtext = "",
    subtextStyle = "",
    defaultOpen = true,
    customContainerStyle = "",
    customMainStyle = "",
    customClickableStyle = "",
    children,
}) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className={`border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm ${customMainStyle}`}>
            {/* Header / toggle */}
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`dropdown-section-toggle w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors focus:outline-none ${customClickableStyle}`}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-row gap-2">
                        {Icon && (
                            <Icon className={`w-5 h-5 flex-shrink-0 ${iconStyle}`} />
                        )}
                        <span className={`font-semibold text-gray-800 ${titleStyle}`}>
                            {title}
                        </span>
                    </div>
                    <span className={`font-base text-gray-800 ${subtextStyle}`}>
                        {subtext}
                    </span>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                />
            </button>

            {/* Collapsible body — grid trick: animates 0fr↔1fr with no JS measuring */}
            <div
                style={{
                    display: "grid",
                    gridTemplateRows: open ? "1fr" : "0fr",
                    transition: "grid-template-rows 220ms ease",
                }}
            >
                <div style={{ overflow: "hidden" }}>
                    <div className={`p-4 border-t border-gray-100 ${customContainerStyle}`}>
                        {typeof children === "function" ? children() : children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DropdownSection;
